import { useMemo } from "react";
import * as THREE from "three";
import { ARCH, ARCHES } from "../../lib/world";
import { BAND } from "../../lib/sections";

export function Wall({ a, b, n, H, mat, holes = [] }) {
  const L = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const tx = (b[0] - a[0]) / L;
  const tz = (b[1] - a[1]) / L;
  const rot = Math.atan2(n[0], n[1]);
  const pieces = [];
  let s = 0;
  [...holes].sort((p, q) => p.c - q.c).forEach((hl) => {
    const e = hl.c - hl.w / 2;
    if (e > s + 0.01) pieces.push([s, e, 0, H]);
    if (H - hl.h > 0.01) pieces.push([e, hl.c + hl.w / 2, hl.h, H]);
    s = hl.c + hl.w / 2;
  });
  if (L > s + 0.01) pieces.push([s, L, 0, H]);
  return pieces.map(([s0, s1, y0, y1], i) => {
    const m = (s0 + s1) / 2;
    return (
      <mesh key={i} position={[a[0] + tx * m, (y0 + y1) / 2, a[1] + tz * m]} rotation={[0, rot, 0]} material={mat}>
        <planeGeometry args={[s1 - s0, y1 - y0]} />
      </mesh>
    );
  });
}

const R = ARCH.w / 2;
const SPRING = ARCH.h - R;
const archFill = (() => {
  const s = new THREE.Shape();
  s.moveTo(-R, -SPRING);
  s.lineTo(R, -SPRING);
  s.lineTo(R, 0);
  s.absarc(0, 0, R, 0, Math.PI, false);
  s.lineTo(-R, -SPRING);
  return s;
})();
export const archTap = (id, onEnter) => ({
  onClick: (e) => {
    e.stopPropagation();
    if (e.delta <= 10) onEnter(id);
  },
  onPointerOver: () => (document.body.style.cursor = "pointer"),
  onPointerOut: () => (document.body.style.cursor = ""),
});

const backLabel = (() => {
  let tex = null;
  return () => {
    if (tex) return tex;
    const c = document.createElement("canvas");
    c.width = 1024;
    c.height = 220;
    tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    const draw = () => {
      const g = c.getContext("2d");
      g.clearRect(0, 0, 1024, 220);
      g.fillStyle = "rgba(255,250,244,0.8)";
      g.beginPath();
      g.roundRect(40, 20, 944, 180, 90);
      g.fill();
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.fillStyle = "#4A2D55";
      g.font = '600 84px "Caveat"';
      g.fillText("← back to the Feeling Wall", 512, 112);
      tex.needsUpdate = true;
    };
    draw();
    document.fonts.load('600 84px "Caveat"').then(draw).catch(() => {});
    return tex;
  };
})();

// Inside each room, its doorway becomes a soft portal back to the Feeling Wall.
export function ReturnPortal({ id, onBack }) {
  const a = ARCHES[id];
  const mats = useMemo(() => ({
    veil: new THREE.MeshBasicMaterial({ color: new THREE.Color(BAND[id]).lerp(new THREE.Color("#FFF6E8"), 0.55), transparent: true, opacity: 0.92, toneMapped: false }),
    glow: new THREE.MeshBasicMaterial({ color: BAND[id], transparent: true, opacity: 0.6, depthWrite: false, toneMapped: false, blending: THREE.AdditiveBlending }),
    label: new THREE.MeshBasicMaterial({ map: backLabel(), transparent: true, toneMapped: false, depthWrite: false }),
  }), [id]);
  const tap = archTap("hall", onBack);
  return (
    <group position={[a.x - a.nx * 0.06, 0, a.z - a.nz * 0.06]} rotation={[0, Math.atan2(-a.nx, -a.nz), 0]} userData={{ returnPortal: id }}>
      <mesh position={[0, SPRING, 0]} material={mats.veil} {...tap}>
        <shapeGeometry args={[archFill, 24]} />
      </mesh>
      <mesh position={[0, SPRING, 0.02]} material={mats.glow}>
        <torusGeometry args={[R + 0.06, 0.05, 6, 40, Math.PI]} />
      </mesh>
      <mesh position={[0, ARCH.h + 0.5, 0.03]} material={mats.label} {...tap}>
        <planeGeometry args={[2.6, 0.56]} />
      </mesh>
    </group>
  );
}
